/**
 * @file: The right-click menu on the week grid.
 *
 * An operator repeats two actions more than any others. The first is to put a
 * takeover over an interval that is already there. The second is to remove a
 * mistake. A create needs a drag, and an edit needs a click, so both actions
 * cost extra steps. This menu puts both at the position of the cursor.
 *
 * The shared menu in client/common/context-menu.js holds the DOM, the position,
 * the delegated listener and the closing rules. This file holds only what the
 * calendar knows: which element was clicked, and what the entries do.
 *
 * The listener sits on the calendar root, and the element stands in the
 * component wrapper beside it. The zoom code and the measuring code query
 * inside `calendar.el`, and a menu in there would match those queries.
 */

import { ContextMenu } from 'common/context-menu'
import { calendarEventId } from 'common/calendar-event-id'
import { intervalIdOf } from '../calendar-event-element'
import { TAKEOVER_MINUTES, NEW_INTERVAL_MINUTES } from '../new-interval-times'

/**
 * True when a long press on a touch screen sent the right-click.
 *
 * On a touch screen a long press drags a bar or marks a new interval. Android
 * Chrome also sends a contextmenu event for the same press, about half a second
 * before the drag starts. The menu then opened over the drag. A tap opens the
 * panel, which has the same commands, so a touch press opens no menu.
 *
 * Chrome sends contextmenu as a PointerEvent with a pointerType. The source
 * capabilities are the older signal in Chrome. Other browsers give neither,
 * and a mouse gives neither, so the menu opens as before.
 */
function isTouchPress (domEvent) {
  if (!domEvent) {
    return false
  }

  if (domEvent.pointerType === 'touch') {
    return true
  }

  return Boolean(domEvent.sourceCapabilities && domEvent.sourceCapabilities.firesTouchEvents)
}

export class GridContextMenu {
  constructor (calendarManager) {
    this.calendarManager = calendarManager
    this.controller = calendarManager.controller
    this.menu = new ContextMenu({
      host: () => this._host(),
      // The listener runs outside Angular. Without this call the panel does not
      // repaint.
      onAfterRun: () => this.controller._$scope.$applyAsync()
    })
  }

  /** Listen on the calendar root. Call this after the instance exists. */
  attach () {
    const root = this.calendarManager.calendar && this.calendarManager.calendar.el

    this.menu.attach(root, (target, domEvent) => this.itemsAt(target, domEvent))
  }

  /** Remove the listener and the element. Called before the calendar is destroyed. */
  detach () {
    this.menu.detach()
  }

  /**
   * The entries for the element under the cursor. Null keeps the menu of the
   * browser, which the time axis, the toolbar and the panel need.
   *
   * @param {Element} target - The element the operator clicked
   * @param {MouseEvent} domEvent - The right-click, which holds the point
   * @returns {Array|null} The entries, or null
   */
  itemsAt (target, domEvent) {
    if (!this.calendarManager.calendar || isTouchPress(domEvent)) {
      return null
    }

    const eventEl = target.closest('.fc-event')
    const inGrid = target.closest('.fc-timegrid-body')

    if (!eventEl && !inGrid) {
      return null
    }

    const at = this.calendarManager.timeAtPoint(domEvent.clientX, domEvent.clientY)

    return eventEl ? this.itemsForEvent(eventEl, at) : this.itemsForGrid(at)
  }

  /**
   * The menu entries for an existing interval.
   *
   * The takeover entry needs a time. A point outside the measured slots and
   * columns gives no time, so the menu then shows the other two entries only.
   */
  itemsForEvent (eventEl, at) {
    const shownId = intervalIdOf(eventEl)
    const calendarEvent = shownId ? this.calendarManager.calendar.getEventById(calendarEventId(shownId)) : null
    const intervalId = calendarEvent && calendarEvent.extendedProps
      ? calendarEvent.extendedProps.intervalId
      : null

    if (!intervalId) {
      return []
    }

    const items = []

    if (at) {
      items.push({
        mark: { icon: 'fa-bolt' },
        label: 'Add a takeover here',
        run: () => this.controller.inspectNew(at, endAfter(at, TAKEOVER_MINUTES), 'takeover')
      })
    }

    items.push({
      mark: { icon: 'fa-pen' },
      label: 'Edit',
      run: () => this.controller.inspect(calendarEvent)
    })

    items.push({
      mark: { icon: 'fa-trash' },
      label: 'Delete',
      // deleteInterval shows its own confirmation.
      run: () => this.controller.deleteInterval({ intervalId })
    })

    return items
  }

  /** The menu entries for an empty part of the grid. */
  itemsForGrid (at) {
    if (!at) {
      return []
    }

    return [{
      mark: { icon: 'fa-plus' },
      label: 'New interval here',
      run: () => this.controller.inspectNew(at, endAfter(at, NEW_INTERVAL_MINUTES))
    }]
  }

  // ===== Internals =====

  /** The wrapper of the component, which is beside the calendar element. */
  _host () {
    const root = this.controller._$element && this.controller._$element[0]

    return (root && root.querySelector('#schedule-calendar-wrapper')) ||
      document.getElementById('schedule-calendar-wrapper')
  }
}

/** The default end time for an interval that the menu creates. */
function endAfter (start, minutes) {
  return new Date(start.getTime() + (minutes * 60 * 1000))
}
